import './SpecifyCalculationDialog.css';
import React, { useEffect, useCallback } from 'react';
import { withTranslate } from 'react-redux-multilingual';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import {
    ReflexContainer,
    ReflexSplitter,
    ReflexElement
} from 'react-reflex';
import Editor from 'react-simple-code-editor';
import { highlight, languages } from 'prismjs/components/prism-core';
import 'prismjs/components/prism-clike';
import 'prismjs/components/prism-javascript';
import 'prismjs/themes/prism.css';
import SearchIcon from '@mui/icons-material/Search';
import IconButton from '@mui/material/IconButton';
import InputBase from '@mui/material/InputBase';
import Tooltip from '@mui/material/Tooltip';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Collapse from '@mui/material/Collapse';
import Typography from '@mui/material/Typography';

import {
    GetFunctions,
} from '../../../services/Workspace.service';

function SpecifyCalculationDialog(props) {
    const { translate, open, scriptsteps, options, handleClose, handleAgree } = props;

    const [calculation, setCalculation ] = React.useState();
    const [searchOutputKeys, setSearchOutputKeys] = React.useState("");
    const [searchFunctions, setSearchFunctions] = React.useState("");
    const [errorMsg, setErrorMsg] = React.useState("");

    const curPosRef = React.useRef({start: 0, end: 0});
    const textareaRef = React.useRef(); 

    const operators = [
        { name: translate('concatenate'), operator: '""' },
        { name: translate('parentheses'), operator: '()' },
        { name: translate('equalto'), operator: ' = ' },
        { name: translate('notequalto'), operator: ' ≠ ' },
        { name: translate('greaterthan'), operator: ' > ' },
        { name: translate('lessthan'), operator: ' < ' },
        { name: translate('greaterthanorequalto'), operator: ' ≥ ' },
        { name: translate('lessthanorequalto'), operator: ' ≤ ' },
        { name: translate('carriagereturn'), operator: '¶' },
        { name: translate('plus'), operator: ' + ' },
        { name: translate('minus'), operator: ' - ' },
        { name: translate('multiply'), operator: ' * ' },
        { name: translate('divide'), operator: ' / ' },
        { name: translate('and'), operator: ' and ' },
        { name: translate('or'), operator: ' or ' },
        { name: translate('not'), operator: ' not ' },
    ]

    const [functions, setFunctions] = React.useState([]);

    const handleClickAgree = async() => {
        if(!await validateCalculation(calculation)) return;
        handleAgree(options, calculation);
    }

    const handleOnClick = async (value) => {
        if(value){
        if(calculation){
            await setCalculation(calculation.slice(0, curPosRef.current.start) + value + calculation.slice(curPosRef.current.end));
        } else {
            await setCalculation(value);
        }
        textareaRef.current = document.querySelector('.editor textarea');
        await textareaRef.current.focus();
        curPosRef.current.start = curPosRef.current.end = (value === "\"\"" || value === "()") ? curPosRef.current.start + value.length - 1 : curPosRef.current.start + value.length;
        textareaRef.current.selectionStart = curPosRef.current.start;
        textareaRef.current.selectionEnd = curPosRef.current.end;
        }
    }

    const filterOutputKeys = (searchValue) => {
        return scriptsteps?.filter((step) => step?.output?.key?.toLowerCase().indexOf(searchValue?.toLowerCase()) > -1 && step?.output?.icon)
                          ?.filter((arr, index, self) => index === self.findIndex((t) => (t.output?.key === arr.output?.key)))
    }

    const filterFunctions = (category, searchValue) => {
        return functions?.filter((func) => func.category === category && func?.name?.toLowerCase().indexOf(searchValue?.toLowerCase()) > -1)
    }    

    const handleClickExpand = React.useCallback(
        (i) => {
            let _functions = JSON.parse(JSON.stringify(functions));
            _functions[i].expanded = !_functions[i].expanded;
            setFunctions(_functions);
        },
        [functions],
    )

    const convertOperators = (value) => {
        value = value.replace(/\r\n|\r|\n/g,"");
        value = value.replace(/=(?=[^\"]*(?:\"[^\"]*\"[^\"]*)*$)/g, "==");
        value = value.replace(/≠(?=[^\"]*(?:\"[^\"]*\"[^\"]*)*$)/g, "!=");
        value = value.replace(/≥(?=[^\"]*(?:\"[^\"]*\"[^\"]*)*$)/g, ">=");
        value = value.replace(/≤(?=[^\"]*(?:\"[^\"]*\"[^\"]*)*$)/g, "=<");
        value = value.replace(/and(?=[^\"]*(?:\"[^\"]*\"[^\"]*)*$)\b/g, "&&");
        value = value.replace(/or(?=[^\"]*(?:\"[^\"]*\"[^\"]*)*$)\b/g, "||");
        value = value.replace(/not(?=[^\"]*(?:\"[^\"]*\"[^\"]*)*$)\b/g, "!");
        // console.log(value);
        return value;
    }

    const validateCalculation = (value) => {
        return new Promise((resolve, reject) => {
            setErrorMsg("");
            if(value) {
                value = convertOperators(value);
                // console.log(value);
                let _scriptsteps = scriptsteps.filter(step => step.output.key);
                let _outputKeys = value?.match(/\$[a-zA-Z0-9_]{1,50}(?=[^\"]*(?:\"[^\"]*\"[^\"]*)*$)\b/g) || [];
                for (let _key of _outputKeys) {
                    if(!_scriptsteps.find(step => step.output.key === _key)) {
                        setErrorMsg(translate('msg_thespecifiedfieldcannotbefound', { caculation: _key }));
                        resolve(false);
                    }
                    value = value?.replace(new RegExp("\\" + _key + "(?=[^\\\"]*(?:\\\"[^\\\"]*\\\"[^\\\"]*)*$)\\b", "g"), "\"\"")
                }
                let _functions = value?.match(new RegExp("(?<=^([^\"]|\"[^\"]*\")*)[A-Za-z]+(?=\())", "g"));
                for (let _func of _functions || []) {
                    if(!functions.find(func => func.formula.split("(")[0] === _func)) {
                        setErrorMsg(translate('msg_thespecifiedfieldcannotbefound', { caculation: _func }));
                        resolve(false);
                    }
                    value = value?.replace(new RegExp("(?<=^([^\"]|\"[^\"]*\")*)[A-Za-z]+(?=\())", "g"), "\"\"")
                }
                value = value?.replace(/'(?:\\.|[^'\\])*'/g, "")?.replace(/"(?:\\.|[^"\\])*"/g, "")?.replace(/[,+\-*/=!<>&|¶≠≥≤]/g, "")?.replace(/\d/g, "");

                if((value.match(/\(/g) || []).length > (value.match(/\)/g) || []).length) {
                    setErrorMsg(translate('msg_missing', { name: ')' }));
                    resolve(false);
                } else if ((value.match(/\(/g) || []).length < (value.match(/\)/g) || []).length) {
                    setErrorMsg(translate('msg_missing', { name: '(' }));
                    resolve(false);
                }
                if((value.match(/\[/g) || []).length > (value.match(/\]/g) || []).length) {
                    setErrorMsg(translate('msg_missing', { name: ']' }));
                    resolve(false);
                } else if ((value.match(/\[/g) || []).length < (value.match(/\]/g) || []).length) {
                    setErrorMsg(translate('msg_missing', { name: '[' }));
                    resolve(false);
                }
                if((value.match(/\{/g) || []).length > (value.match(/\}/g) || []).length) {
                    setErrorMsg(translate('msg_missing', { name: '}' }));
                    resolve(false);
                } else if ((value.match(/\{/g) || []).length < (value.match(/\}/g) || []).length) {
                    setErrorMsg(translate('msg_missing', { name: '{' }));
                    resolve(false);
                }
                value = value?.replace(/[\(\)\[\]\{\}]|\s/g, "");
                if(value) {
                    setErrorMsg(translate('msg_thespecifiedfieldcannotbefound', { caculation: value }));
                    resolve(false);
                }
            }
            resolve(true);
        })
    }

    useEffect(
        () => {
            GetFunctions()
            .then(res => setFunctions(res.data.datas))
            setErrorMsg("");
            setCalculation("");
            if (options.value && options.value.toString().replace(/\r\n|\r|\n/g, "").match(/^fx\(.*\)$/g)) {
                setCalculation(options.value.toString().replace(/fx\(|\)$/g, ""));
            }
        },
        [open]
    )

    return (
        <Dialog
            fullWidth
            maxWidth="lg"
            open={open}
            onClose={handleClose}
        >
            <DialogTitle sx={{ pb: 0 }}>
            {translate('specifycalculation')}
            </DialogTitle>
            <DialogContent sx={{ pb: 0 }}>
                <Typography variant="body1" sx={{ textAlign: "left", pl: 1 }}>
                    ※{translate('textismustbeinbetweendoublequotation')}
                </Typography>
                <Box className="calculation">
                    <ReflexContainer orientation="vertical">
                        <ReflexElement
                            minSize="0"
                            size="300"
                        >
                            <Box p={1}>
                                <Box className='search-box'>
                                    <Box sx={{ p: 0, maxWidth: "1000px" }}>
                                        <IconButton type="submit" aria-label="search">
                                            <SearchIcon />
                                        </IconButton>
                                        <InputBase
                                            type="search"
                                            sx={{ flex: 1 }}
                                            placeholder={translate('search')}
                                            onChange={(e) => setSearchOutputKeys(e.target.value)}
                                            value={searchOutputKeys}
                                        />
                                    </Box>
                                </Box>
                                <List component="div" disablePadding>
                                    {filterOutputKeys(searchOutputKeys)?.map((step, index) => (
                                        <ListItem
                                            key={`option-${index}`}
                                            button
                                            onClick={() => handleOnClick(step.output.key)}
                                            className='output'
                                        >
                                            <ListItemIcon 
                                                sx={{ minWidth: 25 }}
                                                >
                                                <span 
                                                    className="material-symbols-outlined" 
                                                    style={{ fontSize: 18 }}
                                                >{step.output.icon}
                                                </span>
                                            </ListItemIcon>
                                            <ListItemText primary={step.output.key} />
                                        </ListItem>
                                    ))}
                                </List>
                            </Box>
                        </ReflexElement>
                        <ReflexSplitter/>
                        <ReflexElement
                            minSize="300"
                            style={{ overflow: "auto" }}
                        >
                            <Box className='editor'>
                                <Editor
                                    value={calculation}
                                    onValueChange={code => setCalculation(code)}
                                    highlight={code => highlight(code, languages.js)}
                                    padding={10}
                                    onMouseOut={(e) => { 
                                        curPosRef.current = { 
                                            start: e.target.selectionStart, 
                                            end: e.target.selectionEnd 
                                        }
                                    }}
                                    style={{
                                        minHeight: "498px",
                                        backgroundColor: "aliceblue",
                                    }}
                                />
                            </Box>
                        </ReflexElement>
                        <ReflexSplitter className='operators'>
                            {operators?.map((item, index) => (
                                <Tooltip key={`operator-${index}`} title={item.name} arrow placement="left">
                                    <Button size="small" onClick={() => { handleOnClick(item.operator) }}>
                                    {item.operator}
                                    </Button>
                                </Tooltip>
                            ))}
                        </ReflexSplitter>
                        <ReflexElement
                            minSize="0"
                            size="300"
                        >
                            <Box p={1}>
                                <Box className='search-box'>
                                    <Box sx={{ p: 0, maxWidth: "1000px" }}>
                                        <IconButton type="submit" aria-label="search">
                                            <SearchIcon />
                                        </IconButton>
                                        <InputBase
                                            type="search"
                                            sx={{ flex: 1 }}
                                            placeholder={translate('search')}
                                            onChange={(e) => setSearchFunctions(e.target.value)}
                                            value={searchFunctions}
                                        />
                                    </Box>
                                </Box>
                                <List component="div" disablePadding>
                                    {(functions?.filter((v, i, a) => a.findIndex(t => (t.category === v.category)) === i))?.map((func, i) => (
                                    <Box key={`func_category_${i}`}>
                                        <ListItem 
                                            button 
                                            onClick={() => handleClickExpand(i)}
                                            className="category"
                                        >
                                            <ListItemText primary={translate(func.category)} />
                                            {functions[i].expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                        </ListItem>
                                        <Collapse in={functions[i].expanded} timeout="auto" unmountOnExit>
                                            <List component="div" disablePadding>
                                                {filterFunctions(func.category, searchFunctions)?.map((item, index) => (
                                                    <ListItem
                                                        key={`func-${index}`}
                                                        button
                                                        className="function"
                                                    >
                                                        <ListItemText primary={translate(`fx${item.name}`)} onClick={() => handleOnClick(item.formula) } />
                                                    </ListItem>
                                                ))
                                                }
                                            </List>
                                        </Collapse>
                                    </Box>))}
                                </List>
                            </Box>
                        </ReflexElement>
                    </ReflexContainer>
                </Box>
            </DialogContent>
            <DialogActions sx={{ px: "33px" }}>
                <Box sx={{ flex: 2, textAlign: "left" }}>
                    <Box sx={{ color: 'error.main' }}>
                        {errorMsg && <Box className='error-message'><PriorityHighIcon color='error' /> {errorMsg}</Box>}
                    </Box>
                </Box>
                <Button variant="contained" size="small" disableElevation color='secondary' onClick={handleClose}>
                {translate('cancel')}
                </Button>
                <Button variant="contained" size="small" disableElevation color="info" onClick={handleClickAgree}>
                {translate('ok')}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default withTranslate(SpecifyCalculationDialog);